import { generateHash, generateNamedHash, HashAlgorithm } from './hash';

export interface TimedCache<TKey, TValue> extends Cache<TKey, TValue> {
  /**
   * Gets an entry in the cache without updating the last accessed time.
   * @returns - The value associated with the key or undefined if the key does not exist
   */
  peek(key: TKey): TValue | undefined;
}

interface Cache<TKey, TValue> {
  /**
   * @returns An iterable of keys in the cache.
   */
  keys(): IterableIterator<string>;
  /**
   * Checks if an entry exists in the cache for the provided key.
   * @returns boolean indicating whether an element with the specified key exists or not.
   */
  has(key: TKey): boolean;
  /**
   * Gets an entry in the cache associated to the provided key otherwise undefined.
   * @returns - The value associated with the key or undefined if the key does not exist
   */
  get(key: TKey): TValue | undefined;
  /**
   * Gets an entry in the cache associated to the provided key.
   * @returns - A coerced value which could be undefined
   */
  getUnsafe(key: TKey): TValue;
  /**
   * Adds a new entry with a specified key and value to the cache. If an element with
   * the same key already exists, the element will be updated.
   * @returns - The value associated with the key
   */
  set(key: TKey, value: TValue): TValue;
  /**
   * @returns - true if an entry in the cache existed and has been removed, or false
   * if the element does not exist.
   */
  delete(key: TKey): boolean;
  /** clears cache entries */
  clear(): void;
}

export class NamedKey<TKey> {
  readonly hash: string;
  constructor(
    public readonly name: string,
    public readonly key: TKey,
    hashAlgorithm = HashAlgorithm.md5,
  ) {
    this.hash = generateNamedHash(name, [key], hashAlgorithm);
  }
}

class NamedKeyCache<TValue> implements TimedCache<NamedKey<string>, TValue> {
  private readonly _cache: TimedCache<string, TValue>;

  constructor(ttlMs?: number) {
    this._cache = cacheFactory.cache<TValue>(ttlMs ?? Infinity);
  }
  keys(): IterableIterator<string> {
    return this._cache.keys();
  }
  peek(key: NamedKey<any>): TValue | undefined {
    return this._cache.peek(key.hash);
  }
  has(key: NamedKey<any>) {
    return this._cache.has(key.hash);
  }
  get(key: NamedKey<any>) {
    return this._cache.get(key.hash);
  }
  getUnsafe(key: NamedKey<string>): TValue {
    return this._cache.getUnsafe(key.hash);
  }
  /**
   * Gets all entries matching the provided name.
   * @returns An array of values associated with the provided name.
   */
  getAllByName(name: string) {
    const results: TValue[] = [];
    for (const key of this._cache.keys()) {
      const keyName = key.split('|')[0];
      if (keyName === name) {
        results.push(this._cache.get(key) as TValue);
      }
    }
    return results;
  }
  set(key: NamedKey<any>, value: TValue) {
    return this._cache.set(key.hash, value);
  }
  delete(key: NamedKey<any>) {
    return this._cache.delete(key.hash);
  }
  /**
   * Deletes all entries matching the provided name.
   * @returns The number of deleted entries.
   */
  deleteAllByName(name: string): number {
    let deletedCnt = 0;
    for (const key of this._cache.keys()) {
      const keyName = key.split('|')[0];
      if (keyName === name && this._cache.delete(key)) {
        deletedCnt++;
      }
    }
    return deletedCnt;
  }
  clear(): void {
    this._cache.clear();
  }
}

class TimedKeyValueCache<TKey, TValue> implements TimedCache<TKey, TValue> {
  private readonly _cache: TimedCache<string, TValue>;
  private readonly _generateHash: (key: TKey) => string;

  constructor(ttlMs: number, hashAlgorithm: (key: string) => string) {
    this._generateHash = (key: TKey) => generateHash([key], hashAlgorithm);
    this._cache = cacheFactory.cache<TValue>(ttlMs);
  }

  keys(): IterableIterator<string> {
    return this._cache.keys();
  }
  has(key: TKey): boolean {
    return this._cache.has(this._generateHash(key));
  }
  peek(key: TKey): TValue | undefined {
    return this._cache.peek(this._generateHash(key));
  }
  get(key: TKey): TValue | undefined {
    return this._cache.get(this._generateHash(key));
  }
  getUnsafe(key: TKey): TValue {
    return this._cache.getUnsafe(this._generateHash(key));
  }
  set(key: TKey, value: TValue): TValue {
    return this._cache.set(this._generateHash(key), value);
  }
  delete(key: TKey): boolean {
    return this._cache.delete(this._generateHash(key));
  }
  clear(): void {
    this._cache.clear();
  }
}

class TimedValueCache<TValue> implements TimedCache<string, TValue> {
  private readonly cache = new Map<string, { lastAccessDate: Date | null; item?: TValue }>();

  constructor(private readonly ttlMs: number) {}

  keys() {
    return this._purgeAndExecute(() => this.cache.keys());
  }
  has(key: string): boolean {
    return this._purgeAndExecute(() => this.cache.has(key));
  }
  peek(key: string): TValue | undefined {
    return this._purgeAndExecute(() => this.cache.get(key)?.item);
  }
  get(key: string): TValue | undefined {
    return this._purgeAndExecute(() => this._get(key));
  }
  getUnsafe(key: string): TValue {
    return this._purgeAndExecute(() => this._get(key) as TValue);
  }
  set(key: string, value: TValue) {
    this.cache.set(key, { lastAccessDate: new Date(), item: value });
    return value;
  }
  delete(key: string) {
    return this._purgeAndExecute(() => this.cache.delete(key));
  }
  clear() {
    this.cache.clear();
  }

  private _get(key: string): TValue | undefined {
    const entry = this.cache.get(key) ?? { lastAccessDate: null, item: undefined };
    this.cache.set(key, { lastAccessDate: new Date(), item: entry.item });
    return entry.item;
  }

  private _purgeAndExecute<T>(fn: () => T): T {
    if (this.ttlMs !== Infinity) {
      purgeExpiredMapEntries(this.cache, this.ttlMs);
    }
    return fn();
  }
}

export interface AccessTracker<TKey> {
  /**
   * @returns an object with the lastAccessDate and rollback function
   */
  get(key: TKey): AccessTrackerItem;
  /**
   * @returns an object with the lastAccessDate and rollback function but will not update cache
   */
  peek(key: TKey): AccessTrackerItem;
  /**
   * deletes lastAccessDate for provided key.
   */
  delete(key: TKey): void;
}

export interface AccessTrackerItem {
  lastAccessDate: Date | null;
  /** High-resolution timestamp */
  hrTime: number | null;
  rollback(): Date | null;
}
interface AccessTrackerCacheItem {
  lastAccessDate: Date | null;
  prev: Date | null;
  hrTime: number | null;
  prevHrTimestamp: number | null;
}
class AccessTrackerCache<TKey> implements AccessTracker<TKey> {
  private readonly _cache = new Map<string, AccessTrackerCacheItem>();
  private readonly _defaultValue: AccessTrackerCacheItem = {
    lastAccessDate: null,
    prev: null,
    hrTime: null,
    prevHrTimestamp: null,
  };
  private readonly _generateHash: (key: TKey) => string;

  constructor(private readonly ttlMs: number = Infinity, hashAlgorithm: (key: string) => string) {
    this._generateHash = (key: TKey) => generateHash([key], hashAlgorithm);
  }

  get(key: TKey): AccessTrackerItem {
    return this._purgeAndExecute(() => {
      const hash = this._generateHash(key);
      const item = this._cache.get(hash) ?? this._defaultValue;
      this._cache.set(hash, {
        lastAccessDate: new Date(),
        prev: item.lastAccessDate,
        hrTime: performance.now(),
        prevHrTimestamp: item.hrTime,
      });
      const updatedItem: AccessTrackerItem = {
        lastAccessDate: item.lastAccessDate,
        hrTime: item.hrTime,
        /**
         * rollback to previous lastAccessDate.
         * @returns lastAccessDate or null on first access
         */
        rollback: () => {
          this._cache.set(hash, {
            lastAccessDate: item.prev,
            prev: item.prev,
            hrTime: item.prevHrTimestamp,
            prevHrTimestamp: item.prevHrTimestamp,
          });
          return item.prev;
        },
      };
      return updatedItem;
    });
  }

  peek(key: TKey): AccessTrackerItem {
    return this._purgeAndExecute(() => {
      const hash = this._generateHash(key);
      const item = this._cache.get(hash) ?? this._defaultValue;
      const updatedItem: AccessTrackerItem = {
        lastAccessDate: item.lastAccessDate,
        hrTime: item.hrTime,
        /**
         * @returns previous lastAccessDate without updating cache
         */
        rollback: () => item.prev,
      };
      return updatedItem;
    });
  }

  delete(key: TKey): void {
    this._purgeAndExecute(() => this._cache.delete(this._generateHash(key)));
  }

  private _purgeAndExecute<T>(fn: () => T): T {
    if (this.ttlMs !== Infinity) {
      purgeExpiredMapEntries(this._cache, this.ttlMs);
    }
    return fn();
  }
}

function purgeExpiredMapEntries<TValue>(
  map: Map<string, { lastAccessDate: Date | null; item?: TValue }>,
  ttlMs: number,
) {
  const now = new Date();
  for (const entry of map.entries()) {
    const [key, item] = entry;
    const diff = (item.lastAccessDate?.getTime() ?? now.getTime()) + ttlMs - now.getTime();
    if (diff <= 0) {
      map.delete(key);
    }
  }
}

function createCacheFactory() {
  return {
    /**
     * Used to cache entries which can be removed if not accessed after a specified time. For example
     * if the `ttlMs` equals 5 minutes and an item is retrieved before time has elapsed, that entry
     * will live for another 5 minutes.
     * @param ttlMs - The ammount of time in milliseconds a cached entry can exist without being accessed.
     * For example, if `get('key')` was called 6 minutes ago and the `ttlMs` equals 5 minutes, that entry
     * will have been deleted.
     * @example ```ts
     * // no expiration
     * const cache = cacheFactory.cache<{data: any}>();
     * // an item will expire after 1 minute of inactivity
     * const cache = cacheFactory.cache<{data: any}>(1000 * 60);
     * // set a value
     * cache.set('key', {data: 'value'});
     * ```
     */
    cache<TValue>(ttlMs?: number): TimedCache<string, TValue> {
      return new TimedValueCache<TValue>(ttlMs ?? Infinity);
    },
    /**
     * Used to cache entries which can be removed if not accessed after a specified time. For example
     * if the `ttlMs` equals 5 minutes and an item is retrieved before time has elapsed, that entry
     * will live for another 5 minutes.
     * @param ttlMs - The ammount of time in milliseconds a cached entry can exist without being accessed.
     * For example, if `get(TKey)` was called 6 minutes ago and the `ttlMs` equals 5 minutes, that entry
     * will have been deleted.
     * @param hashAlgorithm - The hashing algorithm to use for the key (default: md5)
     * @example ```ts
     * // no expiration
     * const cache = cacheFactory.keyValueCache<{key: string}, {value: string}>();
     * // an item will expire after 2 minutes of inactivity
     * const cache = cacheFactory.keyValueCache<{key: string}, {value: string}>(1000 * 60 * 2);
     * // set a value
     * cache.set({key: 'foo'}, {value: 'bar'});
     * ```
     */
    keyValueCache<TKey, TValue>(
      ttlMs?: number,
      hashAlgorithm = HashAlgorithm.md5,
    ): TimedCache<TKey, TValue> {
      return new TimedKeyValueCache<TKey, TValue>(ttlMs ?? Infinity, hashAlgorithm);
    },
    /**
     * Used to cache entries which can be removed if not accessed after a specified time. For example
     * if the `ttlMs` equals 5 minutes and an item is retrieved before time has elapsed, that entry
     * will live for another 5 minutes.
     * @param ttlMs - The ammount of time in milliseconds a cached entry can exist without being accessed.
     * For example, if `get(TKey)` was called 6 minutes ago and the `ttlMs` equals 5 minutes, that entry
     * will have been deleted.
     * @example ```ts
     * // no expiration
     * const cache = cacheFactory.namedKeyCache<{data: any}>();
     * // an item will expire after 2 minutes of inactivity
     * const cache = cacheFactory.namedKeyCache<{data: any}>(1000 * 60 * 2);
     * // set a value
     * cache.set(new NamedKey('name', 'foo'), {data: 'foo'});
     * cache.set(new NamedKey('name', 'bar'), {data: 'bar'});
     * // get all entries with the name 'name' (returns [{data: 'foo'}, {data: 'bar'}])
     * const entries = cache.getAllByName('name');
     * // delete all entries with the name 'name' (returns 2)
     * cache.deleteAllByName('name');
     */
    namedKeyCache<TValue>(ttlMs?: number): TimedCache<NamedKey<string>, TValue> {
      return new NamedKeyCache<TValue>(ttlMs);
    },
    /**
     * Tracks the last time the provided key was accessed.
     * @param ttlMs - Ammount of time between checks a cached item can exist (default=Infinity).
     * For example, if `get()` was called 15 minutes ago and
     * the `ttlMs` equals 14 minutes, that entry will have been deleted.
     * @param hashAlgorithm - The hashing algorithm to use for the key (default: md5)
     * @example ```ts
     * // tracker will reset after 3 minutes of inactivity
     * const tracker = cacheFactory.accessTracker<string>(1000 * 60 * 3);
     * const keyTracker = tracker.get('key');
     * try {
     *   // last time 'key' was retrieved or null if first time
     *   const accessDate = keyTracker.lastAccessDate;
     *   // do stuff...
     * } catch {
     *   // if error occurs rollback to previous lastAccessDate
     *   keyTracker.rollback();
     * }
     * ```
     */
    accessTracker<TKey>(ttlMs?: number, hashAlgorithm = HashAlgorithm.md5): AccessTracker<TKey> {
      return new AccessTrackerCache<TKey>(ttlMs, hashAlgorithm);
    },
  };
}

export type CacheFactory = ReturnType<typeof createCacheFactory>;
export const cacheFactory: CacheFactory = createCacheFactory();
